<template>
    <div role="tablist" class="tabs-wrapper" :class="{'w-full': !vertical && grow}" v-resize="updateArrowsNow">
        <lazy-btn v-if="showLeftArrow" touch-optimized class="absolute none sm:flex left-0 z-1" icon style="transform: translateY(-50%); top: 50%;"
             :aria-label="$t('action.back')" @click="scrollLeft">
            <lazy-icon :icon="icons.back" large force-size/>
        </lazy-btn>
        <div ref="tabs" class="tabs" :class="[vertical ? 'is-vertical' : 'is-horizontal', {'tabs--center': center}]" v-scroll.self="updateArrowsNow">
            <div ref="content" class="tabs__content">
                <slot/>
            </div>
            <span class="absolute z-1"
                  :class="{'w-2': vertical, 'h-2': !vertical, 'top-0 rounded-b-lg': !vertical && reverseBorder, 'bottom-0 rounded-t-lg': !vertical && !reverseBorder, 'right-0 rounded-l-lg': vertical && reverseBorder, 'left-0 rounded-r-lg': vertical && !reverseBorder}"
                  :style="activeBorderStyle"/>

            <span v-if="persistBorder" class="absolute w-full bg-gray-200"
                  :class="{'w-1': vertical, 'h-1': !vertical, 'top-0': !vertical && reverseBorder, 'bottom-0': !vertical && reverseBorder, 'right-0': vertical && reverseBorder, 'left-0': vertical && reverseBorder}"/>
        </div>
        <lazy-btn v-if="showRightArrow" touch-optimized class="absolute none sm:flex right-0 z-1" icon style="transform: translateY(-50%); top: 50%;"
             :aria-label="$t('action.next')" @click="scrollRight">
            <lazy-icon :icon="icons.next" large force-size/>
        </lazy-btn>
    </div>
</template>
<script>
import {provide as RegistrableProvide} from "../../mixins/Registrable";
import TogglesChildren from "../../mixins/TogglesChildren";
import {scrollToPosition} from "@spoferan/spoferan-ts-core";
import {tabInjection} from "../../types/keys";
import {backIcon, nextIcon} from "../../config";

export default {
    name: 'Tabs',

    mixins: [RegistrableProvide(tabInjection), TogglesChildren],

    props: {
        vertical: {
            type: Boolean,
            default: false
        },

        // Whether to grow the tabs over the full width
        grow: {
            type: Boolean,
            default: false
        },

        // Whether the border of the tabs shall be always visible
        persistBorder: {
            type: Boolean,
            default: false
        },

        // Whether the border shall be placed on top for horizontal bars or on right for vertical bars
        reverseBorder: {
            type: Boolean,
            default: false
        },

		// Whether to align the tab item text in the center of each item (for vertical tabs)
		center: {
			type: Boolean,
			default: false
		}
    },

    data() {
        return {
            showLeftArrow: false,
            showRightArrow: false,
            activeBorderStyle: {},
			icons: {
				next: nextIcon,
				back: backIcon,
			}
        }
    },

    watch: {
        activeItemValues() {
			this.refreshActiveTabStyle();
        }
    },

    mounted() {
        this.$nextTick(function () {
            this.updateArrowsNow();
			this.refreshActiveTabStyle();
        });
    },

    updated() {
        this.updateArrowsNow();
    },

    methods: {

		refreshActiveTabStyle() {
			const activeTabIndex = this.getItemIndexByValue(this.activeItemValues);

			this.scrollToCenterTab(activeTabIndex);
			this.refreshActiveBorderStyle(activeTabIndex);
		},

        refreshActiveBorderStyle(activeTabIndex) {
            const activeTab = activeTabIndex > -1 ? this.items[activeTabIndex] : null;

            // We do not update the border style when no tab is focused for a short time
            if (!activeTab) {
                return;
            }

            const style = {
                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                backgroundColor: activeTab.color ?? this.$config.public.colors.primary
            };

            if (this.vertical) {
                style.height = `${this.getHeightOfTab(this.items[activeTabIndex])}px`;
                let top = 0;
                for (let i = 0; i < activeTabIndex; i++) {
                    top += this.getHeightOfTab(this.items[i]);
                }
                style.top = `${top}px`;
            } else {
                style.width = `${this.getWidthOfTab(this.items[activeTabIndex])}px`;
                let left = 0;
                for (let i = 0; i < activeTabIndex; i++) {
                    left += this.getWidthOfTab(this.items[i]);
                }
                style.left = `${left}px`;
            }

            this.activeBorderStyle = style;
        },

        scrollToCenterTab(activeTabIndex) {
            if (activeTabIndex > -1 && this.$refs.tabs) {
                let scrollPosition = 0;
                for (let i = 0; i < activeTabIndex; i++) {
                    scrollPosition += this.getWidthOfTab(this.items[i]);
                }
                // Right here the scroll position would be the beginning of our target tab -> now we want to center it
                if (typeof window !== 'undefined') {
                    scrollPosition = scrollPosition - (window.innerWidth / 2) + (this.getWidthOfTab(this.items[activeTabIndex]) / 2);
                }

                scrollToPosition(Math.max(0, scrollPosition), this.$refs.tabs, true);
            }
        },

        getWidthOfTab(tab) {
            return tab && tab.$el ? tab.$el.getBoundingClientRect().width : 0;
        },

        getHeightOfTab(tab) {
            return tab && tab.$el ? tab.$el.getBoundingClientRect().height : 0;
        },

        updateArrowsNow() {
            if (this.vertical || !this.$el || !this.$refs.content || !this.$refs.tabs) {
                return;
            }

            let parentWidth = this.$el.clientWidth;
            let width = this.$refs.content.clientWidth;
            let scrollPos = this.$refs.tabs.scrollLeft;

            this.showLeftArrow = scrollPos > (this.getWidthOfTab(this.items[0]) / 2);
            this.showRightArrow = width > parentWidth && (scrollPos + parentWidth + (this.getWidthOfTab(this.items[this.items.length - 1]) / 2)) < width;
        },

        scrollLeft() {
            if (!this.$refs.tabs) {
                return;
            }

            let parentWidth = this.$el.clientWidth;
            let scrollPos = this.$refs.tabs.scrollLeft;

            // Just a fix in case the arrows are not displayed correctly
            if (scrollPos <= 0) {
                this.updateArrowsNow();
            } else {
                scrollToPosition(Math.max(0, scrollPos - (parentWidth * 0.75)), this.$refs.tabs, true);
            }
        },

        scrollRight() {
            if (!this.$refs.tabs) {
                return;
            }

            let visibleWidth = this.$el.clientWidth;
            let scrollPos = this.$refs.tabs.scrollLeft;
            let fullWidth = this.$refs.content.clientWidth;

            let endScrollPosition = fullWidth - visibleWidth;
            let nextSlidingWindowPosition = scrollPos + (visibleWidth * 0.75);
            let scrollToPos = Math.min(endScrollPosition, nextSlidingWindowPosition);

            scrollToPosition(scrollToPos, this.$refs.tabs, true);
        }
    }
}
</script>
<style>
.app .tabs-wrapper {
  max-width: 100%;
  position: relative;
}
.app .tabs-wrapper .tabs {
  display: flex;
  contain: content;
}
.app .tabs-wrapper .tabs .tabs__content {
  display: flex;
  flex: 1 0 auto;
  position: relative;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  white-space: nowrap;
}
.app .tabs-wrapper .tabs.is-horizontal {
  overflow-x: auto;
  align-items: flex-end;
}
.app .tabs-wrapper .tabs.is-vertical {
  overflow-y: auto;
  align-items: center;
}
.app .tabs-wrapper .tabs.is-vertical .tabs__content {
  flex-direction: column;
}
</style>